import { toast } from "@birdwatch/ui/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type WaveSurfer from "wavesurfer.js";
import type RecordPlugin from "wavesurfer.js/dist/plugins/record";

import { createRecordingWaveSurfer } from "../_libs/recording-wavesurfer";

export function useAudioRecorder() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const recordRef = useRef<ReturnType<typeof RecordPlugin.create> | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  /** Init wavesurfer */
  useEffect(() => {
    if (!containerRef.current) return;

    // Destroy any existing instance before creating a new one
    wavesurferRef.current?.destroy();
    wavesurferRef.current = null;

    const { wavesurfer, record } = createRecordingWaveSurfer(
      containerRef.current,
    );

    record.on("record-start", () => {
      setIsRecording(true);
      setIsPaused(false);
      setDurationSeconds(0);
      // Clear any previous URL/blob when a new recording starts
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
      setAudioUrl(null);
      setAudioBlob(null);
    });

    // Update duration in real-time while recording (RecordPlugin emits ms)
    record.on("record-progress", (elapsedMs: number) => {
      const seconds = Math.floor((elapsedMs ?? 0) / 1000);
      setDurationSeconds(seconds);
    });

    // Keep local state in sync with plugin pause/resume
    record.on("record-pause", () => {
      setIsPaused(true);
    });
    record.on("record-resume", () => {
      setIsPaused(false);
    });

    record.on("record-end", (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setAudioBlob(blob);
      prevUrlRef.current = url;
      setIsRecording(false);
      setIsPaused(false);
    });

    wavesurferRef.current = wavesurfer;
    recordRef.current = record;

    return () => {
      wavesurfer.destroy();
    };
  }, []);

  /** Start */
  const startRecording = useCallback((options?: { deviceId?: string }) => {
    const deviceIdToUse = options?.deviceId ?? undefined;
    recordRef.current
      ?.startRecording(deviceIdToUse ? { deviceId: deviceIdToUse } : undefined)
      .catch((error) => {
        if (error instanceof Error && error.name === "Error") {
          toast.error({
            title: "Failed to start recording",
            description: error.message,
          });
        }
      });
  }, []);

  /** Stop */
  const stopRecording = useCallback(() => {
    recordRef.current?.stopRecording();
    setIsRecording(false);
    setIsPaused(false);
  }, []);

  /** Pause */
  const pauseRecording = useCallback(() => {
    recordRef.current?.pauseRecording();
    setIsPaused(true);
  }, []);

  /** Resume */
  const resumeRecording = useCallback(() => {
    recordRef.current?.resumeRecording();
    setIsPaused(false);
  }, []);

  /** Toggle pause/resume while recording */
  const toggleRecordingPause = useCallback(() => {
    if (!recordRef.current) return;
    if (recordRef.current.isPaused()) {
      recordRef.current.resumeRecording();
    } else {
      recordRef.current.pauseRecording();
    }
    setIsPaused(!isPaused);
  }, [isPaused]);

  /** Clear current recording data */
  const clearRecording = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    setDurationSeconds(0);
    if (prevUrlRef.current) {
      URL.revokeObjectURL(prevUrlRef.current);
      prevUrlRef.current = null;
    }
  }, []);

  const duration = useMemo(() => durationSeconds, [durationSeconds]);

  return {
    containerRef,
    recordRef,
    duration,
    audioUrl,
    audioBlob,
    isRecording,
    isPaused,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    toggleRecordingPause,
    clearRecording,
  };
}
